import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import ShareButton from "@/components/ShareButton";
import LevelUpModal from "@/components/LevelUpModal";
import { useAuth } from "@/context/AuthContext";
import { BRANCH_META } from "@/lib/branches";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowLeft, Target, DollarSign, Zap, HandHeart, Send, Trophy, X, ExternalLink,
    Info, Check, Ban,
} from "lucide-react";

const STATUS_META = {
    draft: { label: "Awaiting payment", color: "#94A3B8", bg: "#F1F5F9" },
    listed: { label: "Open", color: "#3EB489", bg: "#DBF3E9" },
    claimed: { label: "In progress", color: "#FFC107", bg: "#FFF3CC" },
    submitted: { label: "Under review", color: "#1E88E5", bg: "#DCEBFC" },
    awarded: { label: "Awarded", color: "#6F00FF", bg: "#EADEFF" },
    canceled: { label: "Canceled", color: "#94A3B8", bg: "#F1F5F9" },
};

export default function BountyDetail() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const { user } = useAuth();
    const nav = useNavigate();
    const [b, setB] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [claimNote, setClaimNote] = useState("");
    const [showClaim, setShowClaim] = useState(false);
    const [showSubmit, setShowSubmit] = useState(false);
    const [submissionNote, setSubmissionNote] = useState("");
    const [submissionLink, setSubmissionLink] = useState("");
    const [reward, setReward] = useState(null);

    const load = () => {
        api.get(`/bounties/${id}`).then((r) => setB(r.data)).catch(() => setError("Bounty not found."));
    };

    useEffect(() => { load(); }, [id]);

    // If we just landed from Stripe redirect (?listed=1), poll status once
    useEffect(() => {
        if (params.get("listed") === "1" && b?.status === "draft" && b?.listing_session_id) {
            api.get(`/bounties/status/${b.listing_session_id}`).then((r) => {
                if (r.data.bounty) setB(r.data.bounty);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [b?.status]);

    if (error) return <div className="min-h-screen flex flex-col items-center justify-center gap-4"><h1 className="font-display font-black text-3xl">{error}</h1><Link to="/bounties" className="arcade-btn" data-variant="primary"><Target className="w-4 h-4" /> All bounties</Link></div>;
    if (!b) return <div className="min-h-screen flex items-center justify-center text-slate-500 font-display">Loading bounty…</div>;

    const branch = b.branch || "business";
    const meta = BRANCH_META[branch] || BRANCH_META.business;
    const status = STATUS_META[b.status] || STATUS_META.listed;
    const RewardIcon = b.reward_type === "cash" ? DollarSign : Zap;

    const isOwner = user && String(user.id) === String(b.posted_by);
    const isClaimer = user && String(user.id) === String(b.claimed_by);
    const canClaim = user && b.status === "listed" && !isOwner;
    const canSubmit = user && b.status === "claimed" && isClaimer;
    const canAward = user && b.status === "submitted" && isOwner;
    const canCancel = user && isOwner && ["listed", "claimed"].includes(b.status);

    const doClaim = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post(`/bounties/${id}/claim`, { note: claimNote });
            setB(data.bounty);
            setShowClaim(false);
            setClaimNote("");
            toast.success("Bounty claimed. Get to work.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't claim.");
        } finally {
            setBusy(false);
        }
    };

    const doSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post(`/bounties/${id}/submit`, {
                submission_note: submissionNote,
                submission_link: submissionLink,
            });
            setB(data.bounty);
            setShowSubmit(false);
            setSubmissionNote("");
            setSubmissionLink("");
            toast.success("Submitted. Waiting on the owner.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't submit.");
        } finally {
            setBusy(false);
        }
    };

    const doAward = async () => {
        if (!window.confirm(b.reward_type === "cash" ? `Award this bounty? By awarding you confirm you will pay $${b.amount_usd} directly to ${b.claimed_by_name}. Continue?` : "Award this bounty and transfer the XP stake to the contributor?")) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/bounties/${id}/award`);
            setB(data.bounty);
            if (data.reward) setReward(data.reward);
            toast.success("Awarded. Nice.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't award.");
        } finally {
            setBusy(false);
        }
    };

    const doCancel = async () => {
        if (!window.confirm("Cancel this bounty? Any XP you staked will be refunded (cash listing fee is non-refundable).")) return;
        setBusy(true);
        try {
            await api.post(`/bounties/${id}/cancel`);
            load();
            toast.success("Bounty canceled.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't cancel.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-3xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <Link to="/bounties" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-bounties">
                    <ArrowLeft className="w-4 h-4" /> All bounties
                </Link>

                {b.status === "draft" && (
                    <div className="mt-4 crystal rounded-3xl p-5 border-l-4" style={{ borderColor: BRANCH_META.arts.color, background: `${BRANCH_META.arts.soft}80` }} data-testid="draft-warning">
                        <div className="font-display font-bold text-slate-900 mb-1">Payment required to publish</div>
                        <p className="text-sm text-slate-700">
                            This bounty is still a draft. Complete the ${(b.platform_fee_cents / 100).toFixed(2)} listing fee at Stripe to publish it.
                        </p>
                        {b.listing_checkout_url && (
                            <a href={b.listing_checkout_url} className="arcade-btn mt-3 inline-flex" data-variant="primary" data-testid="pay-listing-fee">
                                <DollarSign className="w-4 h-4" /> Complete payment
                            </a>
                        )}
                    </div>
                )}

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="crystal rounded-3xl p-6 sm:p-8 mt-4 relative overflow-hidden"
                    style={{ background: `radial-gradient(circle at 90% 10%, ${meta.soft} 0%, #fff 60%)` }}
                >
                    <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/projects/${b.project_slug}`} className="chip hover:opacity-80" style={{ background: meta.soft, color: meta.text }}>
                            <Target className="w-3 h-3" /> {b.project_title}
                        </Link>
                        <span className="chip text-[10px]" style={{ background: status.bg, color: status.color }} data-testid="bounty-status">
                            {status.label}
                        </span>
                    </div>
                    <h1 className="font-display font-black text-4xl tracking-tighter text-slate-900 mt-3 leading-none" data-testid="bounty-title">
                        {b.title}
                    </h1>

                    <div className="mt-3 flex justify-end">
                        <ShareButton path={`/bounties/${b.id}`} title={b.title} refCode={user?.referral_code} />
                    </div>

                    <div className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 font-display font-black text-lg"
                        style={{
                            background: b.reward_type === "cash" ? BRANCH_META.trades.soft : BRANCH_META.humanities.soft,
                            color: b.reward_type === "cash" ? BRANCH_META.trades.text : BRANCH_META.humanities.text,
                        }}
                        data-testid="bounty-reward"
                    >
                        <RewardIcon className="w-5 h-5" />
                        {b.reward_type === "cash" ? `$${b.amount_usd} USD` : `${b.amount_xp} XP`}
                    </div>
                    {b.reward_type === "cash" && (
                        <div className="mt-3 rounded-xl bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 leading-relaxed inline-flex items-start gap-2" data-testid="cash-disclaimer">
                            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                Cash payment happens <strong>directly between owner and contributor</strong>, off Project Atlas. The platform charged a 10% listing fee upfront. Arrange payment (bank transfer, PayPal, Venmo…) after work is delivered.
                            </span>
                        </div>
                    )}

                    <p className="text-slate-700 mt-5 leading-relaxed whitespace-pre-wrap" data-testid="bounty-description">
                        {b.description}
                    </p>

                    <div className="mt-5 flex flex-wrap gap-3 text-xs text-slate-500">
                        <span>Posted by{" "}
                            {b.posted_by_username ? (
                                <Link to={`/u/${b.posted_by_username}`} className="font-bold text-slate-800 hover:text-humanities">{b.posted_by_name}</Link>
                            ) : (
                                <span className="font-bold text-slate-800">{b.posted_by_name}</span>
                            )}
                        </span>
                        {b.claimed_by_name && (
                            <span>· Claimed by{" "}
                                {b.claimed_by_username ? (
                                    <Link to={`/u/${b.claimed_by_username}`} className="font-bold text-slate-800 hover:text-humanities">{b.claimed_by_name}</Link>
                                ) : (
                                    <span className="font-bold text-slate-800">{b.claimed_by_name}</span>
                                )}
                            </span>
                        )}
                    </div>

                    {/* Submission block */}
                    {b.submission_note && (
                        <div className="mt-6 rounded-2xl border border-slate-200 bg-white/80 p-4" data-testid="submission-block">
                            <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1">Submission</div>
                            <p className="text-slate-800 whitespace-pre-wrap">{b.submission_note}</p>
                            {b.submission_link && (
                                <a href={b.submission_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-humanities font-bold text-sm mt-2" data-testid="submission-link">
                                    <ExternalLink className="w-3 h-3" /> {b.submission_link.slice(0, 60)}
                                </a>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="mt-6 flex flex-wrap gap-2">
                        {canClaim && (
                            <button onClick={() => setShowClaim(true)} className="arcade-btn" data-variant={branch} data-testid="bounty-claim">
                                <HandHeart className="w-4 h-4" /> Claim this bounty
                            </button>
                        )}
                        {canSubmit && (
                            <button onClick={() => setShowSubmit(true)} className="arcade-btn" data-variant="primary" data-testid="bounty-submit">
                                <Send className="w-4 h-4" /> Submit work
                            </button>
                        )}
                        {canAward && (
                            <button onClick={doAward} disabled={busy} className="arcade-btn" data-variant={branch} data-testid="bounty-award">
                                <Trophy className="w-4 h-4" /> Award bounty
                            </button>
                        )}
                        {canCancel && (
                            <button onClick={doCancel} disabled={busy} className="arcade-btn" data-variant="ghost" data-testid="bounty-cancel">
                                <Ban className="w-4 h-4" /> Cancel
                            </button>
                        )}
                        {b.status === "awarded" && (
                            <div className="chip" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }} data-testid="awarded-badge">
                                <Check className="w-3 h-3" /> Bounty awarded
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>

            <AnimatePresence>
                {showClaim && (
                    <ModalBackdrop onClose={() => setShowClaim(false)}>
                        <motion.form onClick={(e) => e.stopPropagation()} onSubmit={doClaim} initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }} className="crystal rounded-3xl p-6 w-full max-w-md relative">
                            <CloseX onClose={() => setShowClaim(false)} />
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900">
                                Claim this bounty
                            </h2>
                            <p className="text-sm text-slate-600 mt-1">
                                The owner gets pinged the moment you claim. Add a note if you want them to know your angle.
                            </p>
                            <textarea
                                autoFocus
                                className="field-input mt-3 min-h-[100px]"
                                placeholder="Optional: how you plan to tackle this…"
                                value={claimNote}
                                onChange={(e) => setClaimNote(e.target.value)}
                                maxLength={500}
                                data-testid="claim-note"
                            />
                            <div className="mt-4 flex justify-end gap-2">
                                <button type="button" onClick={() => setShowClaim(false)} className="arcade-btn" data-variant="ghost">Cancel</button>
                                <button className="arcade-btn" data-variant={branch} disabled={busy} data-testid="claim-submit">
                                    <HandHeart className="w-4 h-4" /> Claim it
                                </button>
                            </div>
                        </motion.form>
                    </ModalBackdrop>
                )}
                {showSubmit && (
                    <ModalBackdrop onClose={() => setShowSubmit(false)}>
                        <motion.form onClick={(e) => e.stopPropagation()} onSubmit={doSubmit} initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }} className="crystal rounded-3xl p-6 w-full max-w-md relative">
                            <CloseX onClose={() => setShowSubmit(false)} />
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900">
                                Submit your work
                            </h2>
                            <p className="text-sm text-slate-600 mt-1">
                                Tell the owner what you did and link to the artifact so they can review.
                            </p>
                            <textarea autoFocus className="field-input mt-3 min-h-[130px]" placeholder="What you built, decisions you made, anything the reviewer needs to know…" value={submissionNote} onChange={(e) => setSubmissionNote(e.target.value)} maxLength={2000} required data-testid="submission-note" />
                            <input className="field-input mt-2" placeholder="Link (GitHub, Figma, doc, deployment…)" value={submissionLink} onChange={(e) => setSubmissionLink(e.target.value)} maxLength={400} data-testid="submission-link-input" />
                            <div className="mt-4 flex justify-end gap-2">
                                <button type="button" onClick={() => setShowSubmit(false)} className="arcade-btn" data-variant="ghost">Cancel</button>
                                <button className="arcade-btn" data-variant="primary" disabled={busy} data-testid="submit-work">
                                    <Send className="w-4 h-4" /> Submit
                                </button>
                            </div>
                        </motion.form>
                    </ModalBackdrop>
                )}
            </AnimatePresence>

            <LevelUpModal reward={reward} onClose={() => setReward(null)} />
        </div>
    );
}

function ModalBackdrop({ children, onClose }) {
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
            {children}
        </motion.div>
    );
}
function CloseX({ onClose }) {
    return (
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900">
            <X className="w-5 h-5" />
        </button>
    );
}
